// src/hooks/useScaleWrapperBackground.js
//
// ScaleWrapper renders the whole app on a fixed 375x812 canvas, zoomed to
// fit the real device viewport (see ScaleWrapper.jsx). Unless a device's
// aspect ratio matches 375:812 exactly, that leaves a sliver of the
// viewport's own background showing on the sides or at the bottom — see
// the --scale-wrapper-bg comment in ScaleWrapper.css. That sliver is
// invisible on screens whose background is the app's default cream, but
// glaring on screens with a different background (e.g. the dashboard
// empty state's brown).
//
// Separately: iOS Safari tints its OWN chrome (the status-bar strip and,
// in a plain browser tab — not just an installed home-screen app — the
// bottom URL/toolbar bar too) to match <meta name="theme-color">. That's
// a genuinely different mechanism from anything in our CSS or from
// apple-mobile-web-app-status-bar-style (which only applies once the app
// is added to the home screen and launched standalone). index.html hardcodes
// theme-color to the app's cream default, so without updating it here too,
// Safari's own chrome stays cream regardless of what color this hook sets
// everything else to — which is exactly why the empty state's brown never
// reached the status-bar/toolbar area no matter what the page's own
// background was doing.
//
// This hook lets a screen claim both of these — the letterbox sliver and
// Safari's chrome tint — for as long as it's mounted, and puts both back
// to the app default on unmount so the next screen isn't left stale.
//
// Once running inside the native Capacitor shell (Capacitor.isNativePlatform()
// — false in a plain browser tab or a home-screen PWA, true once built
// through Xcode), none of the Safari-specific meta-tag stuff above applies
// at all: there's no separate "standalone vs regular tab" distinction, and
// theme-color/apple-mobile-web-app-status-bar-style are just inert HTML
// meta tags with no effect. Instead, @capacitor/status-bar gives direct,
// consistent control: setOverlaysWebView(true) once (see App.jsx) makes the
// status bar transparent so our own page background already shows through
// it exactly like black-translucent was meant to, and setStyle() here picks
// light or dark status-bar icon/text color depending on whether the
// current screen's background (this hook's `color` argument) is dark
// (brown empty state) or light (everything else), so the icons stay
// readable against whatever's actually behind them.
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

export const SCALE_WRAPPER_DEFAULT_BG = '#F9F5EF';

// Rough perceived-brightness check so icon color follows whatever color is
// actually passed in, rather than hardcoding "brown = light icons" — if a
// future screen passes a different dark/light background here, this still
// picks sensible status-bar icons without needing a matching code change.
function isDarkColor(hex) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
  if (!m) return false;
  const [r, g, b] = m.slice(1).map((h) => parseInt(h, 16));
  // Standard perceived-luminance formula.
  return (0.299 * r + 0.587 * g + 0.114 * b) < 140;
}

export function useScaleWrapperBackground(color = SCALE_WRAPPER_DEFAULT_BG) {
  useEffect(() => {
    document.documentElement.style.setProperty('--scale-wrapper-bg', color);

    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    const prevThemeColor = themeColorMeta?.getAttribute('content') ?? SCALE_WRAPPER_DEFAULT_BG;
    themeColorMeta?.setAttribute('content', color);

    // Style.Dark = white icons (for dark backgrounds), Style.Light = black
    // icons (for light backgrounds) — Capacitor names these for the
    // background they're meant to sit on, not the icon color itself. This
    // was previously inverted (dark bg -> Style.Light/black, light bg ->
    // Style.Dark/white), which is exactly backwards for contrast.
    if (Capacitor.isNativePlatform()) {
      StatusBar.setStyle({ style: isDarkColor(color) ? Style.Dark : Style.Light }).catch(() => {});
    }

    return () => {
      document.documentElement.style.setProperty('--scale-wrapper-bg', SCALE_WRAPPER_DEFAULT_BG);
      themeColorMeta?.setAttribute('content', prevThemeColor);

      if (Capacitor.isNativePlatform()) {
        StatusBar.setStyle({ style: isDarkColor(prevThemeColor) ? Style.Dark : Style.Light }).catch(() => {});
      }
    };
  }, [color]);
}
